import { useState, useRef, useCallback, useContext } from 'react'
import { Box, Container, Card, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import { AutoAwesome, DeleteOutline, SmartToy } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useSnackbar } from 'notistack'
import Context from '../../../../contexts/Context'
import { getToken } from '../../../../services/Main/AuthToken'
import ChatMessage from '../../../../components/AIAssistant/ChatMessage'
import ChatInput from '../../../../components/AIAssistant/ChatInput'
import TypingIndicator from '../../../../components/AIAssistant/TypingIndicator'
import { useEffect } from 'react'

const API_URL = process.env.REACT_APP_API_URL || 'https://api.blocob3.com.br/'

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: 'Olá! Sou o assistente admin do BlocoB3. Posso ajudar com informações sobre condos, faturas, contratos, repasses, usuários e muito mais. Como posso ajudar?',
  timestamp: new Date(),
}

const SUGGESTED_QUESTIONS = [
  'Quantos condos ativos temos na plataforma?',
  'Quais contratos estão vencendo nos próximos 30 dias?',
  'Me dê o resumo financeiro da plataforma',
  'Quais faturas estão vencidas?',
  'Busque o condo "Edifício Aurora" e me diga quantas unidades ele tem',
  'Me mostre os últimos repasses realizados',
]

const MAX_HISTORY = 20

const AdminAIAssistant = () => {
  const { user } = useContext(Context)
  const [messages, setMessages] = useState([WELCOME_MESSAGE])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const getConversationHistory = useCallback((msgs) => {
    const history = msgs
      .filter(m => m.id !== 'welcome')
      .map(m => ({ role: m.role, content: m.content }))
    return history.slice(-MAX_HISTORY)
  }, [])

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return

    const userMessage = { id: Date.now(), role: 'user', content: text, timestamp: new Date() }
    const assistantMessage = { id: Date.now() + 1, role: 'assistant', content: '', timestamp: new Date() }

    setMessages(prev => [...prev, userMessage, assistantMessage])
    setInputValue('')
    setIsLoading(true)

    abortControllerRef.current = new AbortController()

    try {
      const token = getToken()
      const currentMessages = [...messages, userMessage]
      const response = await fetch(`${API_URL}/ai/admin-chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: text,
          conversation_history: getConversationHistory(currentMessages),
        }),
        signal: abortControllerRef.current.signal,
      })

      if (response.status === 429) {
        throw new Error('RATE_LIMIT')
      }

      if (!response.ok) throw new Error(`Erro: ${response.status}`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim()
            if (data === '[DONE]') break
            try {
              const parsed = JSON.parse(data)
              if (parsed.error) {
                enqueueSnackbar(parsed.error, { variant: 'error' })
                setMessages(prev => prev.filter(m => m.content !== ''))
                return
              }
              if (parsed.content) {
                setMessages(prev => {
                  const updated = [...prev]
                  const lastMsg = updated[updated.length - 1]
                  return [...updated.slice(0, -1), { ...lastMsg, content: lastMsg.content + parsed.content }]
                })
              }
            } catch {
              // Ignorar linhas inválidas
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errorMessage = err.message === 'RATE_LIMIT'
          ? 'Limite de perguntas atingido. Tente novamente em alguns minutos.'
          : 'Erro ao processar sua mensagem. Tente novamente.'
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setMessages(prev => prev.filter(m => m.content !== ''))
      }
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, messages, enqueueSnackbar, getConversationHistory])

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setIsLoading(false)
  }, [])

  const clearHistory = useCallback(() => {
    setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }])
  }, [])

  const handleSend = useCallback(() => {
    sendMessage(inputValue)
  }, [inputValue, sendMessage])

  const showSuggestions = messages.length <= 1 && messages[0]?.id === 'welcome'

  return (
    <>
      <Helmet>
        <title>Assistente IA - Admin</title>
      </Helmet>
      <Container maxWidth="md" sx={{ height: 'calc(100vh - 80px)', py: 2, display: 'flex', flexDirection: 'column' }}>
        <Card
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                Assistente IA Admin
              </Typography>
              <Chip label="Suporte" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', fontWeight: 600, fontSize: '0.7rem' }} />
            </Box>
            <Box>
              {messages.length > 1 && (
                <Tooltip title="Nova conversa">
                  <IconButton size="small" sx={{ color: 'inherit' }} onClick={clearHistory}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Messages area */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} user={user} />
            ))}
            {isLoading && messages.length > 0 && messages[messages.length - 1]?.content === '' && (
              <TypingIndicator />
            )}

            {/* Suggested questions */}
            {showSuggestions && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Sugestões de perguntas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {SUGGESTED_QUESTIONS.map((question) => (
                    <Chip
                      key={question}
                      label={question}
                      variant="outlined"
                      size="small"
                      onClick={() => sendMessage(question)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </Card>
      </Container>
    </>
  )
}

export default AdminAIAssistant
